import React from 'react';
import { useTranslation } from 'react-i18next';

import { Tag } from 'common/components';
import { SelectStyled } from 'common/components/form/Select/Select.styled';
import { useFormikField } from 'common/components/form/useFormikField';
import { FieldWrapper } from 'common/components/form/Wrapper';
import { ISelectModel } from 'common/models';

interface SelectTagProps {
  name: string;
  label?: string;
  options: ISelectModel<any>[];
  placeholder?: string;
  disabled?: boolean;
  clearable?: boolean;
  required?: boolean;
  margin?: string;
  showTags?: boolean;
}

export const SelectTag: React.FC<SelectTagProps> = ({
  name,
  label,
  options,
  placeholder,
  disabled,
  clearable,
  required,
  margin,
  showTags = true,
}) => {
  const { t } = useTranslation();
  const { value, setValue, error, hasError } = useFormikField(name);

  const selectedValues: string[] = Array.isArray(value) ? value : [];

  const selectedOptions = options.filter(opt => selectedValues.includes(opt.value));

  const handleChange = (option: ISelectModel<any> | null) => {
    if (option) {
      const newValues = [...selectedValues, option.value];
      setValue(newValues);
    }
  };

  const removeTag = (option: ISelectModel<any>) => {
    const newValues = selectedValues.filter(v => v !== option.value);
    setValue(newValues);
  };

  const availableOptions = options.filter(opt => !selectedValues.includes(opt.value));

  let dynamicPlaceholder = placeholder || t('Select');
  if (selectedOptions.length > 0) {
    dynamicPlaceholder =
      selectedOptions.length === 1
        ? `1 ${t('Option')}`
        : `${selectedOptions.length} ${t('Options')}`;
  }

  return (
    <FieldWrapper
      name={name}
      label={label}
      isRequired={required}
      error={error}
      margin={margin}
      isDisabled={disabled}
    >
      <SelectStyled
        name={name}
        options={availableOptions}
        isMulti={false}
        value={null}
        defaultValue={null}
        onChange={handleChange as any}
        placeholder={dynamicPlaceholder}
        isClearable={clearable}
        isDisabled={disabled}
        controlShouldRenderValue={false}
        closeMenuOnSelect
        classNamePrefix="true"
        menuPortalTarget={document.body}
        menuPosition="fixed"
        styles={{
          menuPortal: base => ({ ...base, zIndex: 9999 }),
        }}
      />

      {showTags && selectedOptions.length > 0 && (
        <div
          style={{
            marginTop: '0.5rem',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.25rem',
          }}
        >
          {selectedOptions.map(opt => (
            <Tag key={opt.value} label={opt.label} onRemove={() => removeTag(opt)} />
          ))}
        </div>
      )}
    </FieldWrapper>
  );
};
